// stahlia-frontend/src/pages/warehouse/InventurDetailPage.tsx
// Inventur-Detail (Spec-Merkliste ③): Fortschritt, Differenzen, offene Plaetze,
// Zaehlungen-Log, "nach Zaehlung bewegt". Kein DR-Push hier (OI-021-Gate).
import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useInventurDetail, useInventurAnalyse, useCloseInventur, type InventurSession, type CloseInventurResult } from '@/api/inventur'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { downloadPdf } from '@/api/warehouse'
import { useAuth } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { InventurAnalyseView } from './InventurAnalyse'
import { analyseCsv, downloadCsv } from '@/lib/inventur-analyse'

function ModeBadge({ mode }: { mode: InventurSession['mode'] }) {
  const label = mode === 'regal' ? 'Regal' : mode === 'produkt' ? 'Produkt' : 'Voll'
  return <span className="text-xs font-semibold">{label}</span>
}

function scopeText(session: InventurSession): string {
  if (session.mode === 'regal') return (session.scope.row_codes ?? []).join(', ') || '—'
  if (session.mode === 'produkt') return `${(session.scope.merchant_skus ?? []).length} Artikel`
  return 'alle Plätze'
}

export default function InventurDetailPage() {
  const { id } = useParams()
  const sessionId = Number(id)
  const { warehouse } = useAuth()
  const detail = useInventurDetail(Number.isFinite(sessionId) ? sessionId : null)
  const analyse = useInventurAnalyse(Number.isInteger(sessionId) && sessionId > 0 ? sessionId : null)
  const close = useCloseInventur(sessionId)
  // Gemeinsamer Pending-Guard und Fehler-Toast für CSV/PDF.
  const [downloading, setDownloading] = useState<'csv' | 'pdf' | null>(null)
  // P2: Kennzahlen des blockierten Abschlusses — im Dialog sichtbar statt im Fliesstext.
  const [blocked, setBlocked] = useState<Extract<CloseInventurResult, { code: 'inventur_unvollstaendig' }> | null>(null)
  async function onDownload(kind: 'csv' | 'pdf', path: string, filename: string) {
    if (downloading) return
    setDownloading(kind)
    try {
      if (kind === 'csv') {
        // Analyse trennt physische Mengen nach Einheit; der Legacy-Export tut das nicht.
        const result = await analyse.refetch()
        if (result.isError || !result.data) throw result.error ?? new Error('Analyse konnte nicht geladen werden')
        downloadCsv(analyseCsv(result.data.artikel), filename)
      } else await downloadPdf(path, filename, undefined, 'GET')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Download fehlgeschlagen')
    } finally {
      setDownloading(null)
    }
  }

  if (detail.isLoading) return <div className="p-4">Lade…</div>
  if (detail.isError) return <div className="p-4 space-y-3" role="alert"><p>Inventur konnte nicht geladen werden: {detail.error.message}</p><Button variant="outline" onClick={() => void detail.refetch()}>Erneut laden</Button> <Link className="underline" to="/warehouse/inventur">Zur Liste</Link></div>
  if (!detail.data) {
    return (
      <div className="p-4">
        Session nicht gefunden. <Link className="text-primary underline" to="/warehouse/inventur">Zurück</Link>
      </div>
    )
  }
  const d = detail.data
  const { session } = d
  const isOpen = session.status === 'offen'
  const canPdf = session.mode === 'voll' || session.status === 'abgeschlossen'

  // OI-116 Stufe 2: Zahl der ERFASSTEN Handregal-Zeilen (nicht der Faecher — mehrere
  // Zeilen koennen auf dasselbe Fach fallen, dort gilt beim Abschluss die letzte).
  // Sie ist die einzige Handregal-Information, die VOR dem Abschluss vorliegt:
  // `korrekturen_geplant` aus der Blockade-Antwort zaehlt nur Paletten-Mengen.
  const handregalZaehlungen = d.counts.filter((c) => c.countType === 'handregal').length

  // P2 (Welle 1b): Der Abschluss meldet sein ERGEBNIS, nicht blosses Gelingen.
  // Prod-Vorfall 2026-07-26 (Session 4): Der Leiter zaehlte das Handregal statt der
  // Palette, schloss ab — und bekam ein gruenes `toast.success` mit "0 Korrektur(en)".
  // Null Korrekturen sind aber genau der Fall, in dem nichts passiert ist. Eine
  // Erfolgsmeldung dafuer ist eine Falschaussage ueber die Wirkung der Handlung.
  // OI-116 Stufe 2: `korrekturen` zaehlt AUSSCHLIESSLICH Paletten-Mengen. Seit der
  // Abschluss zusaetzlich das fuehrende Handregal-Register schreibt, waere das alte
  // pauschale "KEINE Menge geändert" selbst eine Falschaussage — es gibt jetzt eine
  // zweite Wirkung, die der Leiter kennen muss. Deshalb: Aussage auf Paletten
  // eingrenzen UND den zurueckgegebenen `handregal_abgeglichen`-Zaehler nennen.
  function meldeAbschluss(r: { korrekturen: number; klaerfaelle: number; handregal_abgeglichen?: number }) {
    const fachSatz = r.handregal_abgeglichen !== undefined
      ? `Handregal-Register: ${r.handregal_abgeglichen} Fach/Fächer aktualisiert. `
      : ''
    if (r.korrekturen === 0) {
      toast.warning(
        `Abgeschlossen — aber KEINE Paletten-Menge geändert (0 Korrekturen, ${r.klaerfaelle} Klärfall(-fälle)). `
        + fachSatz
        + `Wurde die Palette am Platz gezählt? Eine reine Handregal-Zählung korrigiert keinen Palettenbestand.`,
        { duration: 12000 },
      )
      return
    }
    // Kein Fach-Satz, wenn das Backend die Zahl nicht liefert — „unverändert" waere
    // eine Behauptung ueber etwas, das wir nicht wissen.
    toast.success(
      `Abschluss: ${r.korrekturen} Korrektur(en) an Paletten-Mengen, ${r.klaerfaelle} Klärfall(-fälle). `
      + fachSatz,
    )
  }

  async function onClose() {
    const ok = window.confirm(
      `Inventur #${sessionId} wirklich abschließen? Gezählte Ziele werden geprüft und gebucht. Beim bestätigten Abschluss werden offene, nicht freigegebene Ziele ohne Buchung verworfen. Der Abschluss kann nicht rückgängig gemacht werden.`,
    )
    if (!ok) return
    try {
      const r = await close.mutateAsync(false)
      if ('blocked' in r && r.code === 'recapture_reserved') {
        toast.error(r.message)
        return
      }
      if ('blocked' in r) {
        // A-I9/C-I6 (Welle S2): Backend hat NICHTS geschrieben — der Leiter
        // bestaetigt explizit ein zweites Mal.
        // P2: als echter Dialog statt window.confirm. Im nativen Dialog stand die
        // entscheidende Zahl ("0 automatische Korrekturen") mitten im Fliesstext und
        // war mit einem Klick weg — am 2026-07-26 genau so passiert.
        setBlocked(r)
        return
      }
      meldeAbschluss(r)
    } catch { /* Fehler-Toast kommt aus useCloseInventur().onError */ }
  }

  async function onForceClose() {
    if (!blocked) return
    try {
      const r = await close.mutateAsync(true)
      if ('closed' in r) { meldeAbschluss(r); setBlocked(null) }
      else if (r.code === 'recapture_reserved') { toast.error(r.message); setBlocked(null) }
      else setBlocked(r)
    } catch { /* Fehler-Toast kommt aus useCloseInventur().onError */ }
  }


  return (
    <div className="p-4 space-y-4 max-w-7xl min-w-0 w-full mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">Inventur #{sessionId}</h1>
        <Link className="text-primary underline text-sm" to="/warehouse/inventur">← Liste</Link>
      </div>

      <div className="rounded-xl border border-line bg-panel p-4 flex flex-wrap gap-6 items-center text-sm">
        <div><div className="text-muted">Modus</div><ModeBadge mode={session.mode} /></div>
        <div><div className="text-muted">Scope</div><div>{scopeText(session)}</div></div>
        <div><div className="text-muted">Status</div><div>{session.status}</div></div>
        <div><div className="text-muted">Notiz</div><div>{session.note ?? '—'}</div></div>
        <div className="flex-1" />
        <div className="flex gap-2 flex-wrap">
          {isOpen && warehouse.count && (
            <Button asChild variant="outline">
              <Link to={`/warehouse/inventur/${sessionId}/zaehlen`}>Zählen öffnen</Link>
            </Button>
          )}
          <Button
            variant="outline"
            disabled={downloading !== null}
            onClick={() => void onDownload('csv', `/warehouse/inventur/${sessionId}/export.csv/`, `inventur-${sessionId}.csv`)}
          >
            {downloading === 'csv' ? 'Lädt…' : 'CSV'}
          </Button>
          {canPdf && (
            <Button
              variant="outline"
              disabled={downloading !== null}
              onClick={() => void onDownload('pdf', `/warehouse/inventur/${sessionId}/protokoll.pdf/`, `inventur-protokoll-${sessionId}.pdf`)}
            >
              {downloading === 'pdf' ? 'Lädt…' : 'PDF'}
            </Button>
          )}
          {isOpen && warehouse.lead && (
            <Button onClick={() => void onClose()} disabled={close.isPending}>
              {close.isPending ? 'Schließe…' : 'Abschließen'}
            </Button>
          )}
        </div>
      </div>

      {analyse.isLoading && <p role="status">Analyse wird geladen?</p>}
      {analyse.isError && <div role="alert" className="text-sm text-destructive">Analyse konnte nicht geladen werden: {analyse.error.message}. <Button variant="outline" onClick={() => void analyse.refetch()}>Erneut laden</Button></div>}
      {analyse.data?.artikel && <InventurAnalyseView data={analyse.data} lead={warehouse.lead} reload={async () => {
        const result = await analyse.refetch()
        if (result.isError || !result.data) throw result.error ?? new Error('Analyse konnte nicht geladen werden')
        return result.data
      }} />}

      {/* P2 (Welle 1b): Der unvollstaendige Abschluss als echter Dialog. Die Kennzahlen
          stehen als Liste da, nicht als Fliesstext in einem nativen confirm — und
          "0 geplante Korrekturen" ist eigens hervorgehoben, weil genau diese Zahl am
          2026-07-26 uebersehen wurde und die Inventur wirkungslos blieb. */}
      <Dialog open={blocked !== null} onOpenChange={(o) => { if (!o) setBlocked(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Session unvollständig — trotzdem abschließen?</DialogTitle>
            <DialogDescription>Gezählte Ziele werden geprüft. Noch offene Ziele werden bei Bestätigung ohne Mengenbuchung verworfen.</DialogDescription>
          </DialogHeader>
          {blocked && (
            <div className="space-y-3 text-sm">
              {blocked.korrekturen_geplant === 0 && (
                <p className="rounded-lg border border-warn bg-panel p-3 font-semibold text-ink">
                  Es sind <span className="text-lg">0</span> Korrekturen geplant — dieser Abschluss
                  würde <span className="underline">keine einzige Paletten-Menge ändern</span>.
                  Wurde die Palette am Platz gezählt? Eine reine Handregal-Zählung korrigiert
                  keinen Palettenbestand.
                  {handregalZaehlungen > 0 && (
                    <>{' '}Gültige Handregal-Zählungen werden trotzdem in den Handregal-Bestand
                      übernommen. Unklare Zuordnungen werden als Klärfall gemeldet.</>
                  )}
                </p>
              )}
              <ul className="space-y-1">
                <li className="flex justify-between border-b border-line pb-1">
                  <span>Erfasste Handregal-Zählungen</span>
                  <span className="font-bold">{handregalZaehlungen}</span>
                </li>
                <li className="flex justify-between border-b border-line pb-1">
                  <span>Geplante Korrekturen (Paletten-Mengen)</span>
                  <span className="font-bold">
                    {blocked.korrekturen_geplant}
                  </span>
                </li>
                <li className="flex justify-between border-b border-line pb-1">
                  <span>Offene Plätze (nicht gezählt)</span><span className="font-bold">{blocked.offene_plaetze}</span>
                </li>
                <li className="flex justify-between border-b border-line pb-1">
                  <span>Angeordnete Zweitzählungen</span><span className="font-bold">{blocked.offene_zweitzaehlungen}</span>
                </li>
                {typeof blocked.offene_paletten === 'number' && <li className="flex justify-between"><span>Offene Paletten</span><span>{blocked.offene_paletten}</span></li>}
                {typeof blocked.nicht_zaehlbare_paletten === 'number' && <li className="flex justify-between"><span>Nicht mehr zählbare Paletten</span><span>{blocked.nicht_zaehlbare_paletten}</span></li>}
                {typeof blocked.offene_handregalfaecher === 'number' && <li className="flex justify-between"><span>Ungezählte Handregalfächer</span><span>{blocked.offene_handregalfaecher}</span></li>}
                <li className="flex justify-between">
                  <span>Positionen auf mehreren Plätzen gezählt</span><span className="font-bold">{blocked.doppelte_positionen}</span>
                </li>
              </ul>
              <p className="text-ink-2">Offene, nicht freigegebene Ziele werden ohne Buchung verworfen. Der Abschluss kann nicht rückgängig gemacht werden.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlocked(null)}>Zurück zum Zählen</Button>
            <Button variant="destructive" disabled={close.isPending} onClick={() => void onForceClose()}>
              Trotzdem abschließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
